import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { descreverDocumentoEstrangeiro } from "../utils/documentoEstrangeiro";

// ── Helpers ───────────────────────────────────────────────────────────────────

function getNome(f) {
  if (!f) return "—";

  return f.tipoPessoa === "PJ" ? f.razaoSocial || "—" : f.pfNome || f.razaoSocial || "—";
}

const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

function hojeExtenso() {
  const dt = new Date();

  return `${dt.getDate()} de ${MESES[dt.getMonth()]} de ${dt.getFullYear()}`;
}

function tituloPropostaComercial(proposta) {
  const codigo = proposta?.codigo ? `${proposta.codigo} - ` : "";

  const tipoImovel = String(proposta?.tipoImovelLocacao || "")
    .trim()
    .toUpperCase();

  if (!tipoImovel) {
    return `${codigo}PROPOSTA COMERCIAL`;
  }

  const tipoTitulo = tipoImovel === "TERRENO" ? "DE TERRENO" : tipoImovel;

  return `${codigo}PROPOSTA COMERCIAL DE LOCAÇÃO ${tipoTitulo}`;
}

// ── Estilos ───────────────────────────────────────────────────────────────────

const pageStyle = {
  fontFamily: "Arial, Helvetica, sans-serif",
  fontSize: "9pt",
  lineHeight: "1.2",
  color: "#000",
  backgroundColor: "#fff",
  padding: "8mm 15mm 12mm 15mm",
  maxWidth: "210mm",
  margin: "0 auto",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: "3mm",
  borderBottom: "1px solid #c00",
  paddingBottom: "2mm",
};

const logoStyle = {
  fontSize: "18pt",
  fontWeight: "bold",
  letterSpacing: "-1px",
};

const titleStyle = {
  textAlign: "center",
  fontSize: "11pt",
  fontWeight: "bold",
  letterSpacing: "2px",
  marginBottom: "8mm",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginBottom: "4mm",
  tableLayout: "fixed",
};

const thStyle = {
  backgroundColor: "#d9d9d9",
  color: "#000",
  textAlign: "center",
  fontWeight: "bold",
  fontSize: "9pt",
  lineHeight: "1.2",
  padding: "3px 8px",
  border: "1px solid #ccc",
  letterSpacing: "1px",
  WebkitPrintColorAdjust: "exact",
  printColorAdjust: "exact",
};

const tdLabelBase = {
  width: "42%",
  fontStyle: "italic",
  padding: "3px 8px",
  border: "1px solid #ccc",
  verticalAlign: "top",
  fontSize: "8pt",
  lineHeight: "1.2",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  WebkitPrintColorAdjust: "exact",
  printColorAdjust: "exact",
};

const tdValueBase = {
  width: "58%",
  padding: "3px 8px",
  border: "1px solid #ccc",
  verticalAlign: "top",
  fontSize: "8pt",
  lineHeight: "1.2",
  minHeight: "18px",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  WebkitPrintColorAdjust: "exact",
  printColorAdjust: "exact",
};

const sectionTitleStyle = {
  textAlign: "center",
  fontWeight: "bold",
  fontSize: "9pt",
  letterSpacing: "1px",
  backgroundColor: "#d9d9d9",
  color: "#000",
  border: "1px solid #ccc",
  padding: "3px 8px",
  marginBottom: 0,
  WebkitPrintColorAdjust: "exact",
  printColorAdjust: "exact",
};

const subsectionTitleStyle = {
  textAlign: "left",
  fontWeight: "bold",
  fontSize: "8pt",
  backgroundColor: "#eeeeee",
  color: "#000",
  border: "1px solid #ccc",
  padding: "4px 8px",
  marginTop: "2mm",
  WebkitPrintColorAdjust: "exact",
  printColorAdjust: "exact",
};

const condicoesBoxStyle = {
  border: "1px solid #ccc",
  borderTop: "none",
  padding: "5px 8px",
  marginBottom: "4mm",
  minHeight: "20mm",
  fontSize: "8pt",
  whiteSpace: "pre-wrap",
};

const footerStyle = {
  marginTop: "6mm",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
};

const footerCenterStyle = {
  flex: 3,
  textAlign: "center",
};

const qrCodeBoxStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  fontSize: "7pt",
  color: "#333",
};

const signatureLineStyle = {
  display: "inline-block",
  borderTop: "1px solid #000",
  width: "110mm",
  textAlign: "center",
  marginTop: "8mm",
  paddingTop: "2mm",
  fontSize: "10pt",
  fontWeight: "bold",
};

// ── Estrutura completa do TEMPLATE PC ─────────────────────────────────────────

const TEMPLATE_SECOES = [
  {
    titulo: "DADOS DO IMÓVEL",
    campos: ["Código / Matrícula do Imóvel", "Endereço do Imóvel", "Área do Imóvel"],
  },

  {
    titulo: "DADOS DA LOCAÇÃO DO IMÓVEL",
    campos: ["Complemento", "Área construída m² Matrícula", "Área Construída m² IPTU", "Área Locada?", "Tipo de Locação", "Tipo do Imóvel"],
  },

  {
    titulo: "LOCADORAS",
    campos: ["CPF / CNPJ", "Tipo", "Nome / Razão Social", "E-mail"],
  },

  {
    titulo: "LOCATÁRIAS",
    campos: ["CPF / CNPJ", "Tipo", "Nome / Razão Social", "E-mail"],
  },

  {
    titulo: "PRAZO E DATAS",
    campos: ["Prazo", "Data de Início", "Data de Fim"],
  },

  {
    titulo: "VALORES (R$) — ALUGUEL",
    campos: [
      "Aluguel Mensal",
      "Tipo Vencimento",
      "Dia Vencimento",
      "Mês Reajuste",
      "Índice Reajuste",
      "Tipo 1º Vencimento",
      "1º Vencimento",
      "Carência do Aluguel em Dias",
      "Descrição do Desconto do Aluguel",
    ],
  },

  {
    titulo: "VALORES (R$) — IPTU",
    campos: ["IPTU?", "IPTU", "Tipo Vencimento", "Dia Vencimento", "Mês Reajuste", "1º Vencimento"],
  },

  {
    titulo: "VALORES (R$) — CONDOMÍNIO",
    campos: ["Condomínio?", "Condomínio", "Tipo Vencimento", "Dia Vencimento", "Mês Reajuste", "1º Vencimento"],
  },

  {
    titulo: "VALORES (R$) — ÁGUA E ESGOTO",
    campos: ["Tipo Medição", "Água e Esgoto", "Tipo Vencimento", "Dia Vencimento", "1º Vencimento"],
  },

  {
    titulo: "VALORES (R$) — ENERGIA",
    campos: ["Tipo Medição", "Energia", "Tipo Vencimento", "Dia Vencimento", "1º Vencimento"],
  },

  {
    titulo: "VALORES (R$) — INTERNET",
    campos: ["Internet?", "Internet", "Tipo Vencimento", "Dia Vencimento", "Mês Reajuste", "1º Vencimento"],
  },

  {
    titulo: "VALORES (R$) — TOTEM",
    campos: ["Totem?", "Totem", "Tipo Vencimento", "Dia Vencimento", "Mês Reajuste", "Índice Reajuste", "1º Vencimento"],
  },
];
// ── Componente ────────────────────────────────────────────────────────────────

export default function PropostaPrintTemplate({ proposta, quadroResumo, modoTemplate = false }) {
  if (!proposta) {
    return null;
  }

  const fp = proposta.fichaPessoas ?? [];

  const locatarias = fp.filter((f) => f.campoProposta === "Locataria");

  const nomeLocatarios = locatarias.map(getNome).join(", ") || "—";

  let _rowIdx = 0;

  const Row = ({ label, value, keepCase, vazio = false }) => {
    const bg = _rowIdx++ % 2 === 0 ? "#ffffff" : "#f3e8e6";

    const displayValue = vazio ? "\u00A0" : keepCase || typeof value !== "string" ? value : value?.toUpperCase();

    return (
      <tr>
        <td
          style={{
            ...tdLabelBase,
            backgroundColor: bg,
          }}
        >
          {label}
        </td>

        <td
          style={{
            ...tdValueBase,
            backgroundColor: bg,
          }}
        >
          {displayValue ?? "—"}
        </td>
      </tr>
    );
  };

  // ============================================================
  // TEMPLATE PC
  // ============================================================

  if (modoTemplate) {
    return (
      <div id="print-proposta" style={pageStyle}>
        <style
          dangerouslySetInnerHTML={{
            __html: `
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }

              #print-proposta table thead th {
                background-color: #d9d9d9 !important;
              }

              #print-proposta table tbody tr:nth-child(odd) td {
                background-color: #ffffff !important;
              }

              #print-proposta table tbody tr:nth-child(even) td {
                background-color: #f3e8e6 !important;
              }

              .template-section {
  break-inside: avoid;
  page-break-inside: avoid;
}

.template-caucao {
  padding-top: 12mm;
}

.template-condicoes {
  padding-top: 12mm;
}
            `,
          }}
        />

        {/* ================================================= */}
        {/* CABEÇALHO                                         */}
        {/* ================================================= */}

        <div style={headerStyle}>
          <div
            style={{
              flex: 1,
            }}
          />

          <div
            style={{
              textAlign: "center",
              flex: 2,
            }}
          />

          <div
            style={{
              flex: 1,
              textAlign: "right",
            }}
          >
            <span style={logoStyle}>
              VR
              <span
                style={{
                  color: "#c00",
                }}
              >
                i
              </span>
            </span>
          </div>
        </div>

        <div style={titleStyle}>TEMPLATE — PROPOSTA COMERCIAL</div>

        {/* ================================================= */}
        {/* BLOCOS GERAIS                                    */}
        {/* ================================================= */}

        {TEMPLATE_SECOES.map((secao) => {
          _rowIdx = 0;

          return (
            <div key={secao.titulo} className="template-section">
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th colSpan={2} style={thStyle}>
                      {secao.titulo}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {secao.campos.map((campo) => (
                    <Row key={campo} label={campo} vazio />
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}

        {/* ================================================= */}
        {/* BOLETO                                           */}
        {/* ================================================= */}

        <div className="template-section">
          <div style={sectionTitleStyle}>BOLETO</div>

          <table style={tableStyle}>
            <tbody>
              <Row label="Boleto" value="SIM | NÃO" keepCase />

              <Row label="Quantidade" vazio />

              <Row label="Valor (R$)" vazio />
            </tbody>
          </table>
        </div>

        {/* ================================================= */}
        {/* GARANTIA                                         */}
        {/* ================================================= */}

        <div className="template-section">
          <div style={sectionTitleStyle}>GARANTIA</div>

          <table style={tableStyle}>
            <tbody>
              <Row label="Tipo de Garantia" vazio />

              <Row
                label="Opções"
                value="SEM GARANTIA | CAUÇÃO EM DINHEIRO | SEGURO FIANÇA | FIADOR | CAUÇÃO EM IMÓVEL | TÍTULO DE CAPITALIZAÇÃO | A SER DETERMINADO"
                keepCase
              />
            </tbody>
          </table>
        </div>

        {/* ================================================= */}
        {/* CAUÇÃO EM DINHEIRO                               */}
        {/* ================================================= */}

        <div className="template-section template-caucao">
          <div style={subsectionTitleStyle}>CAUÇÃO EM DINHEIRO</div>

          <table style={tableStyle}>
            <tbody>
              <Row label="Valor da Caução" vazio />

              <Row label="Quantidade de Aluguéis da Caução" vazio />
            </tbody>
          </table>
        </div>

        {/* ================================================= */}
        {/* SEGURO FIANÇA                                    */}
        {/* ================================================= */}

        <div className="template-section">
          <div style={subsectionTitleStyle}>SEGURO FIANÇA</div>

          <table style={tableStyle}>
            <tbody>
              <Row label="CNPJ" vazio />

              <Row label="Nome / Razão Social" vazio />

              <Row label="Documento do Corretor de Seguros" vazio />

              <Row label="Nome / Razão Social do Corretor" vazio />

              <Row label="E-mail do Corretor de Seguros" vazio />
            </tbody>
          </table>
        </div>

        {/* ================================================= */}
        {/* FIADOR                                           */}
        {/* ================================================= */}

        <div className="template-section">
          <div style={subsectionTitleStyle}>FIADOR</div>

          <table style={tableStyle}>
            <tbody>
              <Row label="CPF / CNPJ" vazio />

              <Row label="Tipo" vazio />

              <Row label="Nome / Razão Social" vazio />

              <Row label="E-mail" vazio />

              <Row label="Tipo de Garantia do Fiador" vazio />
            </tbody>
          </table>
        </div>

        {/* ================================================= */}
        {/* IMÓVEL DO FIADOR                                 */}
        {/* ================================================= */}

        <div className="template-section">
          <div style={subsectionTitleStyle}>IMÓVEL DO FIADOR</div>

          <table style={tableStyle}>
            <tbody>
              <Row label="Tipo do Imóvel" vazio />

              <Row label="Cidade do Cartório / RGI" vazio />

              <Row label="Nome do Cartório / RGI" vazio />

              <Row label="RGI" vazio />

              <Row label="Matrícula" vazio />

              <Row label="Cartório" vazio />

              <Row label="Livro" vazio />

              <Row label="Folha" vazio />

              <Row label="Endereço" vazio />

              <Row label="Número" vazio />

              <Row label="Bairro" vazio />

              <Row label="Cidade" vazio />

              <Row label="UF" vazio />

              <Row label="CEP" vazio />

              <Row label="Complemento" vazio />

              <Row label="Link do Imóvel no Google Maps" vazio />
            </tbody>
          </table>
        </div>

        {/* ================================================= */}
        {/* CAUÇÃO EM IMÓVEL                                 */}
        {/* ================================================= */}

        <div className="template-section">
          <div style={subsectionTitleStyle}>CAUÇÃO EM IMÓVEL</div>

          <table style={tableStyle}>
            <tbody>
              <Row label="Tipo do Imóvel" vazio />

              <Row label="RGI" vazio />

              <Row label="Matrícula" vazio />

              <Row label="Cartório" vazio />

              <Row label="Livro" vazio />

              <Row label="Folha" vazio />

              <Row label="Endereço" vazio />

              <Row label="Número" vazio />

              <Row label="Bairro" vazio />

              <Row label="Cidade" vazio />

              <Row label="UF" vazio />

              <Row label="CEP" vazio />

              <Row label="Complemento" vazio />

              <Row label="Link do Imóvel no Google Maps" vazio />

              <Row label="Documentos de Comprovação da Garantia" vazio />
            </tbody>
          </table>
        </div>

        {/* ================================================= */}
        {/* CONDIÇÕES                                        */}
        {/* ================================================= */}

        <div className="template-section template-condicoes">
          <div style={sectionTitleStyle}>CONDIÇÕES</div>

          <div
            style={{
              ...condicoesBoxStyle,
              minHeight: "30mm",
            }}
          />
        </div>

        {/* ================================================= */}
        {/* ASSINATURA                                       */}
        {/* ================================================= */}

        <div style={footerStyle}>
          <div
            style={{
              flex: 1,
            }}
          />

          <div style={footerCenterStyle}>
            <p
              style={{
                fontSize: "9pt",
              }}
            >
              Valinhos, ______ de __________________ de ________.
            </p>

            <div style={signatureLineStyle}>LOCATÁRIO(S)</div>
          </div>

          <div style={qrCodeBoxStyle} />
        </div>
      </div>
    );
  }

  // ============================================================
  // IMPRESSÃO NORMAL DA PC
  // MANTIDA COMO JÁ FUNCIONAVA
  // ============================================================

  return (
    <div id="print-proposta" style={pageStyle}>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }

            #print-proposta table thead th {
              background-color: #d9d9d9 !important;
            }

            #print-proposta table tbody tr:nth-child(odd) td {
              background-color: #ffffff !important;
            }

            #print-proposta table tbody tr:nth-child(even) td {
              background-color: #f3e8e6 !important;
            }
          `,
        }}
      />

      {/* Cabeçalho */}

      <div style={headerStyle}>
        <div
          style={{
            flex: 1,
          }}
        />

        <div
          style={{
            textAlign: "center",
            flex: 2,
          }}
        />

        <div
          style={{
            flex: 1,
            textAlign: "right",
          }}
        >
          <span style={logoStyle}>
            VR
            <span
              style={{
                color: "#c00",
              }}
            >
              i
            </span>
          </span>
        </div>
      </div>

      <div style={titleStyle}>{tituloPropostaComercial(proposta)}</div>

      {/* QUADRO RESUMO */}

      <table style={tableStyle}>
        <thead>
          <tr>
            <th colSpan={2} style={thStyle}>
              QUADRO RESUMO
            </th>
          </tr>
        </thead>

        <tbody>
          {(quadroResumo ?? []).map((linha) => (
            <Row key={linha.label} label={linha.label} value={linha.value} keepCase={linha.keepCase} />
          ))}
        </tbody>
      </table>

      {/* CONDIÇÕES */}

      <div style={sectionTitleStyle}>CONDIÇÕES</div>

      <div style={condicoesBoxStyle}>{proposta.consideracoes || ""}</div>

      {/* Assinatura + QR code */}

      <div style={footerStyle}>
        <div
          style={{
            flex: 1,
          }}
        />

        <div style={footerCenterStyle}>
          <p
            style={{
              fontSize: "9pt",
            }}
          >
            Valinhos, {hojeExtenso()}.
          </p>

          {locatarias.length > 1 ? (
            locatarias.map((loc, idx) => (
              <React.Fragment key={loc.id ?? idx}>
                {idx > 0 && (
                  <>
                    <br />
                    <br />
                    <br />
                    <br />
                  </>
                )}

                <div style={signatureLineStyle}>
                  <div>{getNome(loc)}</div>

                  <div
                    style={{
                      fontWeight: "normal",

                      fontSize: "8pt",

                      marginTop: "1mm",
                    }}
                  >
                    {(!(loc.tipoPessoa === "PJ" ? loc.cnpj : loc.cpf) && descreverDocumentoEstrangeiro(loc)) ||
                      (loc.tipoPessoa === "PJ" ? `CNPJ: ${loc.cnpj || "—"}` : `CPF: ${loc.cpf || "—"}`)}
                  </div>
                </div>
              </React.Fragment>
            ))
          ) : (
            <div style={signatureLineStyle}>Locatários: {nomeLocatarios}</div>
          )}
        </div>

        <div style={qrCodeBoxStyle}>
          {proposta.tokenValidacaoPublica && (
            <QRCodeSVG value={`${window.location.origin}/validar-proposta/${proposta.tokenValidacaoPublica}`} size={70} level="M" />
          )}
        </div>
      </div>
    </div>
  );
}
